import { Location } from '@angular/common';
import { Router } from '@angular/router';
import moment from 'moment-timezone';
import Papa from 'papaparse/papaparse.min.js';
import {PubShareType, PublisherShare} from '../domain/publisher.model';
import { SummedMasterReport } from '../domain/rb/summed-master-report.model';
import {SortUtils} from './sort-utils';
export class Utils {
  private router: Router;

  public static generateGuid() {
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' +
      s4() + '-' + s4() + s4() + s4();
  }

  public static isNullOrUndefined(value) {
    return value === undefined || value === null;
  }

  public static isNullUndefOrEmpty(value) {
    return value === undefined || value === null || value.toString().trim() === '';
  }

  public static randomInt() {
    return Math.random();
  }

  public static isMobile() {
    return /Android|iPhone/i.test(window.navigator.userAgent);
  }

  // Friendly label sent as X-Device-Name so the backend's "Manage your devices" session list
  // has something more readable than the raw userAgent string (which it captures separately anyway).
  public static getBrowserDeviceLabel() {
    const ua = window.navigator.userAgent;

    let browser = 'Browser';
    if (/Edg\//i.test(ua)) {
      browser = 'Edge';
    } else if (/OPR\//i.test(ua)) {
      browser = 'Opera';
    } else if (/Chrome\//i.test(ua)) {
      browser = 'Chrome';
    } else if (/Firefox\//i.test(ua)) {
      browser = 'Firefox';
    } else if (/Safari\//i.test(ua)) {
      browser = 'Safari';
    }

    let os = 'Unknown OS';
    if (/Windows/i.test(ua)) {
      os = 'Windows';
    } else if (/Android/i.test(ua)) {
      os = 'Android';
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      // iOS Safari's UA includes "like Mac OS X", so this must be checked before the macOS
      // pattern below or every iPhone/iPad gets mislabeled as a Mac.
      os = 'iOS';
    } else if (/Mac OS X/i.test(ua)) {
      os = 'macOS';
    } else if (/Linux/i.test(ua)) {
      os = 'Linux';
    }

    return `${browser} on ${os}`;
  }

  public static sortComparatorByAttribute(key: string) {
    return (a, b) => {
      if (a[key] < b[key]) {
        return -1;
      }
      if (a[key] > b[key]) {
        return 1;
      }
      return 0;
    };
  }

  public static exportCsv(csvData, csvExportName?, headerData?) {
    const csv = csvData ? Papa.unparse(csvData) : null;
    const header = headerData ? Papa.unparse(headerData) : null;
    if (csv) {
      const blobParts = [header || '', csv];
      const dateString = new Date().toISOString();
      csvExportName = csvExportName ? `${csvExportName}-${dateString}.csv` : `csv-export-${dateString}.csv`;
      Utils.exportBlob(blobParts, csvExportName);
    }
  }

  public static exportBlobCsv(csv, name?) {
    name = name || `csv-export-${new Date().toISOString()}.csv`;
    Utils.exportBlob([csv], name);
  }

  public static exportBlob(blobParts: any[], name?, type = 'text/csv') {
    const blob = new Blob(blobParts, {type});

    if (!name) {
      const dateString = new Date().toISOString();
      name = 'export-' + dateString + '.csv';
    }
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
      }, 0);
  }

  /**
   * @param obj
   * @param key like 'publisher.widget.type'
   * @return value of object.publisher.widget.type
   */
  static getNestedValue(obj, key) {
    if (key) {
      return key.split('.').reduce((result, key2) => result ? result[key2] : null, obj);
    }
  }

  static getUrlParamsFrom(obj) {
    return Object.keys(obj).reduce((seed, current) => {
      if (seed) {
        seed += '&';
      }
      if (obj[current]) {
        seed += `${current}=${obj[current]}`;
      }
      return seed;
    }, '');
  }

  static pct(a: number, base: number) {
    return (a * 100 / (base || 1));
  }

  // static generateAdUnitTag(adUnit: AdUnit) {
  //   return `<script type="text/javascript" id="${adUnit.widgetId}" src="${environment.cdnUrl}/${adUnit.widgetId}/index.js?cb=%%CACHEBUSTER%%" charset="utf-8" async></script>`;
  // }

  public static convertDateLocaleToUtc(date: string){
    const stillUtc = moment(date).utc();
    return moment(stillUtc).format('YYYY-MM-DDTHH:mm:ss');
  }

  public static convertDateToUtcFromTimezone(date: string, timezone: string){
    const UtcFormatted = moment.tz(date, 'YYYY-MM-DDTHH:mm:ss', timezone).utc().format('YYYY-MM-DDTHH:mm:ss');
    return UtcFormatted;
  }

  public static convertDateUtcToTimezone(date: string, timezone: string){
    const timezoneFormatted = moment.utc(date, 'YYYY-MM-DDTHH:mm:ss').tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
    return timezoneFormatted;
  }

  public static convertDateUTCToLocal(date: string){
    return moment(moment.utc(date).toDate()).local().format('YYYY-MM-DDTHH:mm:ss');
  }

  public static getTimeFromSeconds(durationInSeconds) {
    return durationInSeconds ? this.fancyTimeFormat(durationInSeconds) : '00:00';
  }

  public static fancyTimeFormat(time) {
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    let ret = '';

    if (hrs > 0) {
      ret += '' + hrs + ':';
    }
    ret += '' + (mins < 10 ? '0' + mins : mins) + ':' + (secs < 10 ? '0' : '');
    ret += '' + secs;
    return ret;
  }

  public static parseListOfDomains(domains: string): string[] {
    return Array.from(new Set(domains?.toLowerCase()
      .replace(/;/g, ',')
      .replace(/\n/g, ',')
      .replace(/\t/g, ',')
      .replace(/ /, ',')
      .split(',')))
      .filter(d => d);
  }

  public static getRevenueValue(shares: PublisherShare[], type: PubShareType): number {
    if (shares && shares.length) {
      const filteredVales = shares.filter(item => item.pubShareType === type);
      if (filteredVales.length) {
        filteredVales.sort(SortUtils.propertyComparatorString('day', true));
        return filteredVales[0].value;
      }
    }
    return;
  }

  public static cutLongText(value: string, limit = 140, completeWords = true, ellipsis = ' ...') {
    if (completeWords) {
      limit = value.substr(0, limit).lastIndexOf(' ');
    }
    return value.length > limit ? value.substr(0, limit) + (ellipsis ? ellipsis : '') : value;
  }

  public static formatBites(bytes, decimals = 0): string {
    if (bytes === 0) {
      return '0 b';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['b', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  public static getEmailValidationPattern(){
     
    return /(?:(?:\r\n)?[ \t])*(?:(?:(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*)|(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*:(?:(?:\r\n)?[ \t])*(?:(?:(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*)(?:,\s*(?:(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*|(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)*\<(?:(?:\r\n)?[ \t])*(?:@(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*(?:,@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*)*:(?:(?:\r\n)?[ \t])*)?(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|"(?:[^\"\r\\]|\\.|(?:(?:\r\n)?[ \t]))*"(?:(?:\r\n)?[ \t])*))*@(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*)(?:\.(?:(?:\r\n)?[ \t])*(?:[^()<>@,;:\\".\[\] \x00-\x19]+(?:(?:(?:\r\n)?[ \t])+|\Z|(?=[\["()<>@,;:\\".\[\]]))|\[([^\[\]\r\\]|\\.)*\](?:(?:\r\n)?[ \t])*))*\>(?:(?:\r\n)?[ \t])*))*)?;\s*)/;
  }

  public static getDomainValidationPattern(){
    return /^(?:(?:https?|ftp|smtp):\/\/)?(?:[a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,}.*$/;
  }

  // public static getLivePreviewDomainValidationPattern() {
  //   return /^https?:\/\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&\/=]*)$/;
  // }

  public static getHexColorValidationPattern(){
    return /^#(?:[0-9a-fA-F]{6})$/;
  }

  public static getUnique(arr, comp) {
    // store the comparison  values in array
    const unique = arr.map(e => e[comp])

      // store the indexes of the unique objects
      .map((e, i, final) => final.indexOf(e) === i && i)

      // eliminate the false indexes & return unique objects
      .filter((e) => arr[e]).map(e => arr[e]);
    return unique;
  }

  public static assignParamsToURL(router: Router, location: Location, filter: any){
    const urlTree = router.createUrlTree([], {
      queryParams: Object.assign({}, filter),
      queryParamsHandling: 'merge',
      preserveFragment: true,
    });
    location.replaceState(urlTree.toString());
  }

  public static getRangeOfNumbers(start: number, end: any): number[] {
    const range = Array.from(Array(end - start + 1).keys()).map(x => x + start);
    return range;
  }

  /** If startDate == endDate return true, else false. */
  public static checkDates(data: SummedMasterReport[]): boolean{
    if (data.length > 0) {
      if (data[0].day == data[data.length - 1].day) {
        return true;
      }
      else {false;}
    }
    else {false;}
  }

  public static removeEmptyFromJson(obj) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        const childObject = Utils.removeEmptyFromJson(obj[key]);
        if (childObject === undefined) {
          delete obj[key];
        }
      } else if (obj[key] === '' || obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      }
      if (Array.isArray(obj[key]) && !obj[key][0]) {
        delete obj[key];
      }
    });
    return Object.keys(obj).length > 0 || obj instanceof Array ? obj : undefined;
  };


  /** Removes object from list.  Pass list and object name. */
  public static removeFromList(list, objectName: string) {
    list.splice(list.indexOf(objectName), 1);
  }

  public static getInitialsFromString(data = ''): string {
    const rgx = /(\p{L}{1})\p{L}+/gu;
    const initials = [...data.matchAll(rgx)];
    return (
      (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
  }

  public static removeHtmlTagsFromString(data = ''): string {
    return data?.replace(/<[^>]*>/g, '');
  }

  public static removeWhitespace(data: string) {
    return data.replace(/ /g, '');
  }

  public static scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    document.querySelector('.mat-sidenav-content > .main')?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }
}
